import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
//import { TranslateService } from '@ngx-translate/core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { Subscription, map } from 'rxjs';
import valZip from 'val-zip';
import {
  COUNTRY_OPTIONS,
  GRADE_LEVEL_LIST,
} from '../../../../shared/config/app.constants';
import { ApiService } from '../../../../shared/services/api.service';
import { SchoolMetaData } from '../../../../shared/models/school-meta-data';
import { UsStateData } from '../../../../shared/models/us-state-data';
import { CanadianProvinceData } from '../../../../shared/models/canadian-provinces-data';
import {
  isInValidator,
  maxLengths,
  regexPatterns,
} from '../../../../shared/utilities/validators.utility';
import {
  HowDidYouHearAboutUsResponse,
  mapHowDidYouHearAboutUsOptions,
} from '../../../../shared/utilities/how-did-you-hear-about-us.utility';

export interface LearningQuizNewSchool {
  schoolName: string;
  country: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface LearningQuizFormValue {
  role: string;
  gradeLevel?: string; // Only included when role is 'student'
  howDidYouKnowAboutUs: string;
  schoolMetaDataId?: string; // Only included when role requires a school selected from search
  zipCode?: string; // Only included when role requires a school
  newSchool?: LearningQuizNewSchool; // Only included when the school was added manually; created on quiz submit
}

export const LEARNING_QUIZ_ROLES = {
  Teacher: 'studentLearningQuiz.form.role_teacher',
  Student: 'studentLearningQuiz.form.role_student',
  'Adult Independent Learner':
    'studentLearningQuiz.form.role_adult_independent_learner',
  Other: 'studentLearningQuiz.form.role_other',
};

const STUDENT_ROLE = 'Student';
const SCHOOL_ROLES = ['Teacher', STUDENT_ROLE];
const MANUAL_SCHOOL_FIELDS = [
  'schoolName',
  'schoolstreetaddress',
  'schoolsecondaryaddressunit',
  'schoolcity',
  'schoolstate',
  'zipCode',
];

@Component({
  selector: 'app-learning-quiz-form',
  templateUrl: './learning-quiz-form.component.html',
  styleUrls: ['./learning-quiz-form.component.scss'],
  standalone: false,
  providers: [
    // Isolated TranslateService: this form always renders in English,
    // regardless of the app-level language selection.
    provideTranslateService({
      lang: 'en',
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
  ],
})
export class LearningQuizFormComponent implements OnInit, OnDestroy {
  @Output() formSubmitted = new EventEmitter<LearningQuizFormValue>();

  @ViewChild('quizFormElement') quizFormElement?: ElementRef<HTMLFormElement>;

  quizForm: FormGroup;
  isSubmitted = false;

  roles = LEARNING_QUIZ_ROLES;
  grades = { ...GRADE_LEVEL_LIST };
  references: Record<string, string> = {};
  countries = {
    unitedStates: COUNTRY_OPTIONS.unitedStates,
    canada: COUNTRY_OPTIONS.canada,
  };
  provinces = CanadianProvinceData;

  // School search
  enableAddSchoolMode = false;
  triggeredSchoolSearch = false;
  schools: SchoolMetaData[] = [];
  selectedSchoolMetaData: SchoolMetaData | null = null;
  isSchoolCountryCanada = false;

  // Stops the keyvalue pipe from sorting results on the key
  preserveOriginalOrder = () => 0;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly fb: FormBuilder,
    private readonly _apiService: ApiService,
    //private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.quizForm = this.fb.group({
      role: ['', Validators.required],
      gradeLevel: [''],
      howDidYouKnowAboutUs: ['', Validators.required],
      schoolMetaDataId: [''],
      zipCode: [''],
      schoolcountryregion: [this.countries.unitedStates],
      schoolName: [''],
      schoolstreetaddress: [''],
      schoolsecondaryaddressunit: [''],
      schoolcity: [''],
      schoolstate: [''],
    });
  }

  ngOnInit(): void {
    this.getHowDidYouHearAboutUs();
    this.subscriptions.add(
      this.quizForm.controls['role'].valueChanges.subscribe((role: string) =>
        this.onRoleChange(role)
      )
    );
  }

  get isStudent(): boolean {
    return this.quizForm.controls['role'].value === STUDENT_ROLE;
  }

  get requiresSchool(): boolean {
    return SCHOOL_ROLES.includes(this.quizForm.controls['role'].value);
  }

  onRoleChange(role: string): void {
    const gradeLevel = this.quizForm.controls['gradeLevel'];
    if (role === STUDENT_ROLE) {
      gradeLevel.setValidators(Validators.required);
    } else {
      gradeLevel.clearValidators();
      gradeLevel.setValue('', { emitEvent: false });
    }
    gradeLevel.updateValueAndValidity();

    if (SCHOOL_ROLES.includes(role)) {
      if (!this.enableAddSchoolMode) {
        this.setSchoolSearchValidation();
      }
    } else {
      this.resetSchool();
    }
  }

  hasError(controlName: string): boolean {
    return this.isSubmitted && !!this.quizForm.controls[controlName]?.errors;
  }

  hasControlError(controlName: string, errorKey: string): boolean {
    return (
      this.isSubmitted &&
      !!this.quizForm.controls[controlName]?.hasError(errorKey)
    );
  }

  get hasSchoolSearchError(): boolean {
    return (
      this.hasControlError('schoolMetaDataId', 'required') &&
      !this.triggeredSchoolSearch
    );
  }

  // School search

  onSearchAction(event: { searchEvent: string; searchTerm?: string }): void {
    switch (event.searchEvent) {
      case 'input': {
        this.triggeredSchoolSearch = false;
        break;
      }
      case 'submit': {
        this._apiService
          .getSchoolMetaData(event.searchTerm ?? '')
          .pipe(
            map((res) =>
              (res?.schoolMetaDataList ?? []).map((school: SchoolMetaData) => ({
                schoolMetaDataId: school.schoolMetaDataId,
                schoolName: school.schoolName,
                zipCode: school.zipCode,
              }))
            )
          )
          .subscribe({
            next: (schools: SchoolMetaData[]) => {
              this.schools = schools;
              this.triggeredSchoolSearch = true;
            },
            error: (_) => {
              this.schools = [];
              this.triggeredSchoolSearch = true;
            },
          });
        break;
      }
      case 'clear': {
        this.schools = [];
        this.triggeredSchoolSearch = false;
        break;
      }
    }
  }

  onSchoolSelect(school: SchoolMetaData): void {
    this.selectedSchoolMetaData = school;
    this.quizForm.patchValue({
      schoolMetaDataId: school.schoolMetaDataId,
      zipCode: school.zipCode,
    });
  }

  onSearchNewSchool(): void {
    this.triggeredSchoolSearch = false;
    this.selectedSchoolMetaData = null;
    this.quizForm.patchValue({ schoolMetaDataId: '', zipCode: '' });
  }

  trackBySchoolId(_: number, school: SchoolMetaData): string {
    return school.schoolMetaDataId;
  }

  onManualAdd(): void {
    this.enableAddSchoolMode = true;
    this.triggeredSchoolSearch = false;
    this.selectedSchoolMetaData = null;
    this.quizForm.patchValue({ schoolMetaDataId: '', zipCode: '' });
    this.setManualSchoolValidation();
  }

  returnToSearch(): void {
    this.enableAddSchoolMode = false;
    this.onSearchNewSchool();
    this.clearManualSchoolFields();
    this.setSchoolSearchValidation();
  }

  onSchoolAddressCountryChange(event: { value: string }): void {
    this.isSchoolCountryCanada = event.value === this.countries.canada;
    const schoolstate = this.quizForm.controls['schoolstate'];
    schoolstate.setValue('');
    schoolstate.setValidators([
      Validators.required,
      isInValidator(
        this.isSchoolCountryCanada
          ? this.provinces.map((province) => province.canadianProvinceCode)
          : UsStateData.map((state) => state.stateCode)
      ),
    ]);
    schoolstate.updateValueAndValidity();
    this.quizForm.controls['zipCode'].updateValueAndValidity();
  }

  isValidZipSchool(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ?? '';
      if (value.length === 0) {
        return null;
      }
      if (this.isSchoolCountryCanada) {
        return value.match(regexPatterns.zipCanada)
          ? null
          : { inValidZip: true };
      }
      const isValidUSZip =
        valZip(value, 'US') && value.match(regexPatterns.zipUsFormat);
      return isValidUSZip ? null : { inValidZip: true };
    };
  }

  private setSchoolSearchValidation(): void {
    const schoolMetaDataId = this.quizForm.controls['schoolMetaDataId'];
    schoolMetaDataId.setValidators(Validators.required);
    schoolMetaDataId.updateValueAndValidity();
  }

  private setManualSchoolValidation(): void {
    const controls = this.quizForm.controls;
    controls['schoolName'].setValidators([
      Validators.required,
      Validators.maxLength(maxLengths.schoolName),
      Validators.pattern(regexPatterns.name),
    ]);
    controls['schoolstreetaddress'].setValidators([
      Validators.required,
      Validators.maxLength(maxLengths.streetAddress),
      Validators.pattern(regexPatterns.name),
    ]);
    controls['schoolsecondaryaddressunit'].setValidators([
      Validators.maxLength(maxLengths.secondaryAddressUnit),
      Validators.pattern(regexPatterns.name),
    ]);
    controls['schoolcity'].setValidators([
      Validators.required,
      Validators.maxLength(maxLengths.city),
      Validators.pattern(regexPatterns.name),
    ]);
    controls['zipCode'].setValidators([
      Validators.required,
      this.isValidZipSchool(),
    ]);
    MANUAL_SCHOOL_FIELDS.forEach((field) =>
      controls[field].updateValueAndValidity()
    );

    controls['schoolMetaDataId'].clearValidators();
    controls['schoolMetaDataId'].updateValueAndValidity();

    this.onSchoolAddressCountryChange({
      value: this.quizForm.value.schoolcountryregion,
    });
  }

  private clearManualSchoolFields(): void {
    MANUAL_SCHOOL_FIELDS.forEach((field) => {
      const control = this.quizForm.controls[field];
      control.clearValidators();
      control.setValue('');
    });
    this.quizForm.controls['schoolcountryregion'].setValue(
      this.countries.unitedStates
    );
    this.isSchoolCountryCanada = false;
  }

  private resetSchool(): void {
    this.enableAddSchoolMode = false;
    this.onSearchNewSchool();
    this.clearManualSchoolFields();
    const schoolMetaDataId = this.quizForm.controls['schoolMetaDataId'];
    schoolMetaDataId.clearValidators();
    schoolMetaDataId.updateValueAndValidity();
  }

  // Submit

  /**
   * Submits the form from outside of it (e.g. the Start Assessment button).
   * Errors are rendered before the native submit event fires so that, when
   * focusInvalidInput moves focus to the first invalid field, its error text
   * is already in the DOM and announced via aria-describedby.
   */
  submit(): void {
    this.isSubmitted = true;
    this.cdr.detectChanges();
    this.quizFormElement?.nativeElement.requestSubmit();
  }

  onSubmit(): void {
    this.isSubmitted = true;
    if (this.quizForm.invalid) {
      return;
    }

    this.emitFormValue();
  }

  private emitFormValue(): void {
    const {
      role,
      gradeLevel,
      howDidYouKnowAboutUs,
      schoolMetaDataId,
      zipCode,
    } = this.quizForm.value;
    const formValue: LearningQuizFormValue = {
      role,
      howDidYouKnowAboutUs,
    };
    if (role === STUDENT_ROLE) {
      formValue.gradeLevel = gradeLevel;
    }
    if (SCHOOL_ROLES.includes(role)) {
      formValue.zipCode = zipCode;
      if (this.enableAddSchoolMode) {
        // The school is created when the quiz is submitted, after the reCAPTCHA is completed
        formValue.newSchool = this.buildNewSchool();
      } else {
        formValue.schoolMetaDataId = schoolMetaDataId;
      }
    }
    this.formSubmitted.emit(formValue);
  }

  private buildNewSchool(): LearningQuizNewSchool {
    const value = this.quizForm.value;
    return {
      schoolName: value.schoolName,
      country: value.schoolcountryregion,
      streetAddress1: value.schoolstreetaddress,
      streetAddress2: value.schoolsecondaryaddressunit,
      city: value.schoolcity,
      state: value.schoolstate,
      zipCode: value.zipCode,
    };
  }

  getHowDidYouHearAboutUs(): void {
    //const locale = this.translateService.getCurrentLang() || this.translateService.getFallbackLang() || 'en';
    const locale = 'en';  // Temporary hardcoded locale until the learning library supports multiple locales
    this._apiService.getHowDidYouHearAboutUs(locale).subscribe({
      next: (response: HowDidYouHearAboutUsResponse) => {
        this.references = mapHowDidYouHearAboutUsOptions(response);
      },
      error: (_) => {
        this.references = {};
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
